<template>
    <Head title="Update Product" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Update Product - {{ product.name }}
            </h2>
        </template>
        <BreadCrumbs :items="items"></BreadCrumbs>
        <div class="w-full my-6 overflow-hidden py-12 shadow">
            <div class="px-6 pt-4 pb-2">
                <form @submit.prevent="submit" class="w-full">
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 md:mb-4">
                        <div>
                            <admin-app-label>Name</admin-app-label>
                            <input-basic
                                placeholder="Name"
                                :value="product.name"
                                :error="errors['name']"
                                disabled="disabled"
                                class="border-grey-100 hover:cursor-not-allowed"
                            />
                            <input-error v-if="errors['name']">
                                {{ errors.name }}
                            </input-error>
                        </div>
                        <div>
                            <admin-app-label>Brand</admin-app-label>
                            <input-select
                                placeholder="Brand"
                                labelKey="Product"
                                v-model="form.brand_id"
                                :options="brands"
                                :error="errors['brand_id']"
                                label="name"
                                :reduce="brand => brand.id"
                            >
                            </input-select>
                        </div>
                        <div>
                            <admin-app-label>Superseded by</admin-app-label>
                            <input-select
                                placeholder="Superseded by"
                                labelKey="Product"
                                v-model="form.superseded_by_product_id"
                                :options="comparisons"
                                :error="errors['products']"
                                label="name"
                                :reduce="brand => brand.id"
                            >
                            </input-select>
                            <small class="tracking-wide">The next generation of this product</small>
                        </div>
                        <div>
                            <admin-app-label>Sort Order</admin-app-label>
                            <input-basic
                                placeholder="Sort Order"
                                v-model="form.sort_order"
                                :error="errors['sort_order']"
                                type="number"
                            >
                            </input-basic>
                            <small>
                                Set the sort order that this product will use when displayed in lists on its brand page.
                                Higher numbers will cause the product to take priority over ones with lower sort orders.
                            </small>
                        </div>

                        <div v-if="product.type === 'tyre' && product.classification">
                            <div class="lg:mt-10 lg:mb-3">
                                <InputCheckbox v-model="form.best_in_class">
                                    Best in Class?
                                </InputCheckbox>

                                <SimpleBadge>
                                    {{ product.classification }}
                                </SimpleBadge>
                            </div>

                            <small>
                                Mark this product as best in its class. This will override any other tyres
                                in the same class currently set as best.
                            </small>
                        </div>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Image</admin-app-label>
                            <image-upload
                                :key="`Image`"
                                :label="`Image`"
                                v-model="form.image"
                                :error="errors['image']"
                            />
                            <div v-if="product.image" class="my-4">
                                <admin-app-label>Current Image</admin-app-label>
                                <img class="object-contain p-3 border-solid border-2 border-grey-200 rounded"
                                     :src="product.image"
                                     :alt="product.name"/>
                            </div>
                        </div>
                    </div>

                    <add-videos v-model="form.videos" :errors="errors"></add-videos>

                    <admin-app-label class="my-4">Awards</admin-app-label>
                    <div v-if="product.badges.length"
                         v-for="(award, counter) in product.badges"
                         v-bind:key="counter"
                         class="relative">
                        <div class="grid grid-cols-2 gap-4">
                            <div class="absolute right-0 -top-4">
                                <label @click="removeAward(counter)"
                                       class="block tracking-wider uppercase cursor-pointer mt-2">
                                    <font-awesome-icon icon="minus" class="mr-3"/>
                                    Remove Award</label>
                            </div>
                            <div class="mb-6 md:mb-4">
                                <admin-app-label>Award Name</admin-app-label>
                                <input-select
                                    placeholder="Award Name"
                                    label-key="name"
                                    v-model="form.badges[counter].id"
                                    :options="awardOptions"
                                    :error="errors['post_id']"
                                    label="name"
                                    :reduce="awardOptions => (awardOptions.id)">
                                </input-select>
                            </div>
                            <div class="mb-6 md:mb-4">
                                <admin-app-label>Award Order</admin-app-label>
                                <input-basic
                                    type="number"
                                    placeholder="Award Order"
                                    v-model="form.badges[counter].pageorder.order"
                                />
                                <small>
                                    Set the sort order for this award. Awards with 0 & 1 will appear on the tyre page.
                                </small>
                            </div>
                        </div>
                    </div>
                    <div v-else v-for="(award, increment) in form.badges" v-bind:key="increment" class="relative">
                        <div v-if="counter >= 1" class="absolute right-0 -top-4">
                            <label @click="removeAward(increment)" class="block tracking-wider uppercase cursor-pointer mt-2"><font-awesome-icon icon="minus" class="mr-3" /> Remove Award</label>
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Award Name</admin-app-label>
                            <input-basic
                                placeholder="Award Name"
                                v-model="award.name"
                            />
                        </div>
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Award Order</admin-app-label>
                            <input-basic
                                type="number"
                                placeholder="Award Order"
                                v-model="award.order"
                            />
                            <small>
                                Set the sort order for this award. Awards with 0 & 1 will appear on the tyre page.
                            </small>
                        </div>
                    </div>
                    <div>
                        <label @click="addMore()" class="block tracking-wider uppercase cursor-pointer mt-2">
                            <font-awesome-icon icon="plus" class="mr-3"/>
                            Add More</label>
                    </div>
                    <div class="mt-6 mb-6 md:mb-4">
                        <admin-app-label>Description</admin-app-label>
                        <input-wysiwyg
                            :value="product.description"
                            v-model="form.description"
                        ></input-wysiwyg>
                        <input-error v-if="errors['content']">
                            {{ errors.content }}
                        </input-error>
                    </div>
                    <div class="mb-6 md:mb-4">
                        <admin-app-label>Compared With</admin-app-label>
                        <input-select
                            multiple
                            searchable
                            placeholder="Products"
                            labelKey="Product"
                            v-model="form.products"
                            :options="comparisons"
                            :value="selected"
                            :error="errors['products']"
                            label="name"
                            :reduce="product => product.id"
                        >
                        </input-select>
                    </div>
                    <seo-tags v-model="form.seo_tags" :errors="errors"></seo-tags>
                    <app-button type="submit" class="mt-8">Update</app-button>
                </form>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>

<script>
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import AppButton from "@/Components/AppButton";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import InputWysiwyg from "@/Components/Admin/Forms/Inputs/InputWysiwyg";
import ImageUpload from "@/Components/Admin/Forms/Inputs/ImageUpload";
import InputCheckbox from "@/Components/Forms/Inputs/InputCheckbox";
import Label from "@/Components/Label";
import SimpleBadge from "@/Components/SimpleBadge";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated";
import {Head, useForm} from "@inertiajs/inertia-vue3";
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import InputError from "@/Components/Forms/Inputs/InputError";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";
import AddVideos from "@/Components/Admin/Forms/AddVideos";
import SeoTags from "@/Components/Admin/Forms/SeoTags";

export default {
    name: "Edit",
    components: {
        SimpleBadge,
        InputCheckbox,
        SeoTags,
        AddVideos,
        BreadCrumbs,
        AdminAppLabel,
        AppButton,
        ImageUpload,
        InputBasic,
        InputSelect,
        FontAwesomeIcon,
        Label,
        BreezeAuthenticatedLayout,
        Head,
        InputWysiwyg,
        InputError,
    },
    props:
    {
        product: Object,
        awardOptions: Object,
        brands: Object,
        comparisons: Object,
        errors: Object
    },
    data: function()
    {
        return {
            items: [
                { title: 'Products', path: '/admin/products' },
            ],
            selected: [],
            videoTypes: [
                { label: "YouTube", value: "youtube"},
                { label: "Vimeo", value: "vimeo"}
            ],
            form: this.$inertia.form(
                {
                    ...this.product,
                    _method: "put",
                    image: null,
                    awards: (this.product.badges !== null) ? this.product.badges : [{name: "", image: ""}],
                    badges: this.product.badges ?? [],
                    products: (this.product.comparisons !== null) ? this.product.comparisons : "",
                    videos: this.product.videos ?? [],
                    sort_order: this.product.sort_order,
                    best_in_class: this.product.best_in_class
                }
            )
        }
    },
    mounted() {
        var valueToPush = {};
        valueToPush.title = "#" + this.product.id;
        valueToPush.path = "/admin/products/" + this.product.id + "/edit";

        this.items.push(valueToPush);

        var chosen_products = {};
        this.product.comparisons.forEach(product => {
            chosen_products = product.id;
            this.selected.push(chosen_products);
        });
    },
    methods: {
        addMore() {
            this.form.awards.push(
                {
                    name: "",
                    pageorder: { order: null }
                }
            );
        },
        removeAward(counter)
        {
            const awards = this.form.awards;
            awards.splice(counter, 1);

            this.form.awards = awards;
        },
        submit()
        {
            this.form.post(this.route("admin.products.update", this.product));
        }
    }
}
</script>

<style scoped>

</style>
